// DAS-2141: Shared tooltip descriptions for fundamental analysis fields.
// Sourced from field_metadata.json, FactorCorrelationsTab (DAS-1912),
// and investment_quality_analysis docs. Reused across PAModal_Fundamentals,
// AccountPortfolioModal, and other views that display these metrics.

export const FUNDAMENTAL_FIELD_DESCRIPTIONS = {
  fundamental_final_score: {
    name: 'Final Score',
    description: 'Composite fundamental score. 70% reported facts + 30% (forward estimate × credibility).',
    calculation: '0.7 × Trend Score + 0.3 × (Forward Score × Credibility)',
    scale: 'Range: −100 to +100. Positive = improvement, negative = deterioration.',
  },
  fundamental_trend_score: {
    name: 'Trend Score',
    description: 'Backward-looking score based on reported fundamentals: EPS, revenue, margins, ROE, and free cash flow trends.',
    calculation: 'Weighted composite of YoY changes in key financial metrics.',
    scale: 'Range: 0–100 (percentage of maximum achievable score).',
  },
  fundamental_forward_score: {
    name: 'Forward Score',
    description: 'Forward-looking composite based on analyst consensus estimates, guidance trends, and forward margins.',
    calculation: 'FY+1 analyst consensus growth (EPS, revenue). Scale: −100 to +100.',
    scale: 'Not reported results — based on forward estimates only.',
  },
  fundamental_credibility: {
    name: 'Credibility',
    description: 'Confidence weight for the forward score. Higher when more fundamental positives and sector outperformance; lower when negatives dominate.',
    calculation: 'Proxy score in range [0, 1]. Increased by sector outperformance, decreased by negative trends.',
    scale: 'Range: 0.00 to 1.00.',
  },
  fundamental_trend_label: {
    name: 'Label',
    description: 'Human-readable classification of the current fundamental trend, derived from the final score.',
    scale: 'Strong Improvement → Moderate Improvement → Mild Improvement → Stable → Mild Deterioration → Moderate Deterioration → Strong Deterioration.',
  },
  risk_adj_q_return: {
    name: 'Risk-Adj Q Return',
    description: 'Model score divided by the stock\'s typical random swing — a comparison measure, not a verified quarterly forecast; the proven signal is at the one-year horizon.',
    calculation: 'Expected Q Return ÷ Residual Volatility.',
    scale: 'Dimensionless ratio. Positive = expected gain exceeds volatility.',
  },
  expected_q_return: {
    name: 'Expected Q Return',
    description: 'Relative score: sector base return plus the fundamentals bonus. For comparing stocks; predictive power is proven at one year, not the quarter (DAS-2877).',
    calculation: 'Base Sector Return + Quarterly Alpha from Fundamentals.',
    scale: 'Expressed as a decimal (e.g., 0.05 = 5%).',
  },
  return_probability: {
    name: 'Return Probability',
    description: 'Fundamentals-strength ranking shown as a percentage. Not a literal quarterly probability (disproven on our own data, DAS-2877); the proven odds are at one year: best fifth of stocks higher 59% of the time, worst fifth 39%.',
    calculation: 'Normal CDF(Expected Return ÷ Residual Volatility) × 100.',
    scale: 'Range: 0–100%. Higher = more likely positive outcome.',
  },
  residual_volatility: {
    name: 'Residual Volatility',
    description: 'The portion of quarterly price volatility NOT explained by fundamental factors.',
    calculation: 'Quarterly Volatility × √(1 − Composite Correlation²).',
    scale: 'Expressed as %. Lower = fundamentals explain more of the price movement.',
  },
  PE: {
    name: 'Price to Earnings (P/E)',
    description: 'Compares stock price to earnings per share. A valuation metric showing how much investors pay per unit of earnings.',
    calculation: 'Market Price per Share ÷ Earnings per Share.',
    scale: 'Lower may indicate undervaluation; higher may reflect growth expectations.',
  },
  priceToBook: {
    name: 'Price to Book (P/B)',
    description: 'Compares market value to book value of equity. Indicates how the market values the company relative to its net assets.',
    calculation: 'Market Price per Share ÷ Book Value per Share.',
    scale: 'Lower P/B may indicate undervaluation. Positive correlation suggests quality premium.',
  },
  roe: {
    name: 'Return on Equity (ROE)',
    description: 'Measures profitability relative to shareholder equity. Higher ROE suggests efficient use of equity capital.',
    calculation: 'Net Income ÷ Shareholder Equity × 100.',
    scale: 'Expressed as %. Higher = more profitable use of equity.',
  },
};

// Color mapping for fundamental trend labels (from worst to best)
export const LABEL_COLORS = {
  'Strong Deterioration':   { bg: '#fbe9e7', text: '#bf360c', border: '#e65100' },
  'Moderate Deterioration': { bg: '#fff3e0', text: '#e65100', border: '#f57c00' },
  'Mild Deterioration':     { bg: '#fff8e1', text: '#f57f17', border: '#ffa000' },
  'Stable':                 { bg: '#f5f5f5', text: '#616161', border: '#9e9e9e' },
  'Mild Improvement':       { bg: '#e8f5e9', text: '#2e7d32', border: '#43a047' },
  'Moderate Improvement':   { bg: '#e3f2fd', text: '#1565c0', border: '#1976d2' },
  'Strong Improvement':     { bg: '#e8eaf6', text: '#283593', border: '#3f51b5' },
};

export const getLabelColor = (label) => {
  if (!label) return { bg: '#f5f5f5', text: '#616161', border: '#9e9e9e' };
  const normalized = label.trim();
  return LABEL_COLORS[normalized] || { bg: '#f5f5f5', text: '#616161', border: '#9e9e9e' };
};
